// "Database" in the DFT module: search a structure database on the server - the Crystallography Open Database
// (experimental), an OPTIMADE provider (computed) or the Materials Project (with an API key: the site's, or the user's
// own for this project) - and take one entry: it is then read like an opened structure file.
import { Alert, Anchor, Button, Checkbox, Group, PasswordInput, ScrollArea, SegmentedControl, Select, Stack, Table, Text,
  TextInput, Tooltip } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ErrorBox } from "../../components/Errors";
import { dbFetch, dbList, dbSearch, removeProjectMpKey, setProjectMpKey, type DbId, type DbInfo, type DbKind, type DbQuery,
  type DbRow, type DbSource } from "../../lib/qe";

const PLACEHOLDER: Record<DbKind, string> = {
  formula: "CeO2, Bi2Se3, MoS2", elements: "Bi, Se or sulphur", id: "", text: "cerianite, rutile",
};
const ID_HINT: Record<DbId, string> = { cod: "9009008", optimade: "the provider's id", mp: "mp-149" };

export function DatabaseSearch({ projectId, busy, onPick, onError }: {
  projectId: string; busy: boolean;
  onPick: (name: string, text: string, source: DbSource) => void;
  onError: (e: unknown) => void;
}) {
  const info = useQuery({ queryKey: ["dft-databases", projectId], queryFn: () => dbList(projectId), staleTime: 60_000 });
  const dbs = (info.data?.databases ?? []).filter((d) => d.available);
  const [database, setDatabase] = useState<DbId | null>(null);
  const [provider, setProvider] = useState<string | null>(null);
  const [kind, setKind] = useState<DbKind>("formula");
  const [value, setValue] = useState("");
  const [only, setOnly] = useState(true);
  const [showKey, setShowKey] = useState(false);
  useEffect(() => {                                            // the first database offered, until the user chooses
    if (!database && dbs.length) setDatabase(dbs[0].id);
    if (database && info.data && !dbs.some((d) => d.id === database)) setDatabase(dbs[0]?.id ?? null);
  }, [info.data]);
  const current = dbs.find((d) => d.id === database);
  useEffect(() => {
    if (database === "optimade" && !provider) setProvider(current?.providers?.[0] ?? null);
    if (kind === "text" && database !== "cod") setKind("formula");
  }, [database, info.data]);

  const search = useMutation({
    mutationFn: (q: DbQuery) => dbSearch(q),
  });
  const take = useMutation({
    mutationFn: (row: DbRow) => dbFetch({ database: database!, provider: database === "optimade" ? provider : null,
      project_id: projectId, id: row.id }),
    onSuccess: (f) => { onError(null); onPick(f.filename, f.text, f.source); },
    onError,
  });
  const mp = info.data?.databases.find((d) => d.id === "mp");
  const submit = () => {
    if (!database || !value.trim()) return;
    search.mutate({ database, provider: database === "optimade" ? provider : null, project_id: projectId, kind,
      value: value.trim(), only });
  };
  const kinds = [{ value: "formula", label: "Formula" }, { value: "elements", label: "Elements" }, { value: "id", label: "ID" },
    ...(database === "cod" ? [{ value: "text", label: "Name" }] : [])];

  return (
    <Stack gap={6} data-testid="database-search">
      <Text size="xs" c="dimmed">Search a structure database on the server and take one entry: the recipe is then made complete
        as for a structure file (cell, band path, pseudopotentials, cutoffs, k-mesh).</Text>
      <ErrorBox error={info.error} />
      {info.data && !dbs.length && <Alert color="gray" p="xs">No structure database is offered on this server.</Alert>}
      {dbs.length > 0 && (
        <>
          <Group gap="xs" align="flex-end">
            <Select size="xs" w={230} label="Database" value={database} allowDeselect={false}
              data={dbs.map((d) => ({ value: d.id, label: d.name }))}
              onChange={(v) => { setDatabase(v as DbId); search.reset(); }} />
            {database === "optimade" && (
              <Select size="xs" w={240} label="Provider" value={provider} allowDeselect={false}
                data={current?.providers ?? []} onChange={(v) => { setProvider(v); search.reset(); }} />
            )}
          </Group>
          {current && <Text size="xs" c="dimmed">{current.about}{current.id === "mp" && current.key === "project"
            ? " Using your API key for this project." : ""}</Text>}
          <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
            <Group gap="xs" align="flex-end">
              <SegmentedControl size="xs" value={kind} data={kinds} onChange={(v) => setKind(v as DbKind)} aria-label="Search by" />
              <TextInput size="xs" w={190} value={value} onChange={(e) => setValue(e.currentTarget.value)} aria-label="Search for"
                placeholder={kind === "id" ? ID_HINT[database ?? "cod"] : PLACEHOLDER[kind]} />
              <Button size="xs" type="submit" loading={search.isPending} disabled={!value.trim()}>Search</Button>
            </Group>
          </form>
          {kind === "elements" && (
            <Checkbox size="xs" checked={only} onChange={(e) => setOnly(e.currentTarget.checked)}
              label="Only these elements (else: at least these)" />
          )}
        </>
      )}
      {mp?.enabled && (
        <MpKey projectId={projectId} info={info.data!} open={showKey} setOpen={setShowKey} />
      )}
      <ErrorBox error={search.error ?? take.error} />
      {search.data && (
        <DbResults rows={search.data.results} total={search.data.total} more={!!search.data.more}
          taking={take.isPending ? take.variables?.id : undefined} busy={busy} onTake={(r) => take.mutate(r)} />
      )}
    </Stack>
  );
}

function DbResults({ rows, total, more, taking, busy, onTake }: {
  rows: DbRow[]; total: number; more: boolean; taking: string | undefined; busy: boolean; onTake: (r: DbRow) => void;
}) {
  if (!rows.length) return <Text size="xs" c="dimmed" data-testid="db-results">Nothing found: try the elements, or another database.</Text>;
  return (
    <Stack gap={2} data-testid="db-results">
      <Text size="xs" c="dimmed">{rows.length < total || more ? `The first ${rows.length} of ${more ? "more than " : ""}${total}` : `${total}`} found
        {rows.length < total || more ? " - narrow the search to see others" : ""}.</Text>
      <ScrollArea.Autosize mah={320}>
        <Table fz="xs" verticalSpacing={3} striped withRowBorders={false}>
          <Table.Thead><Table.Tr><Table.Th>ID</Table.Th><Table.Th>Formula</Table.Th><Table.Th>Space group</Table.Th>
            <Table.Th>Atoms</Table.Th><Table.Th>About</Table.Th><Table.Th /></Table.Tr></Table.Thead>
          <Table.Tbody>
            {rows.map((r) => (
              <Table.Tr key={r.id}>
                <Table.Td>{r.url ? <Anchor href={r.url} target="_blank" rel="noreferrer" size="xs">{r.id}</Anchor> : r.id}</Table.Td>
                <Table.Td>{r.formula}</Table.Td>
                <Table.Td>{r.spacegroup ?? "–"}</Table.Td>
                <Table.Td>{r.nsites ?? "–"}</Table.Td>
                <Table.Td>
                  {[r.name, r.detail, r.temperature_K != null ? `${r.temperature_K} K` : "",
                    r.abc && r.abc.every((x) => x != null) ? `a, b, c = ${r.abc.map((x) => x!.toFixed(3)).join(", ")} Å` : ""]
                    .filter(Boolean).join(" · ")}
                </Table.Td>
                <Table.Td>
                  <Tooltip label={r.problem ?? ""} disabled={r.usable}>
                    <Button size="compact-xs" variant="light" disabled={!r.usable || busy || (taking != null && taking !== r.id)}
                      loading={taking === r.id} onClick={() => onTake(r)} aria-label={`use ${r.id}`}>Use</Button>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea.Autosize>
    </Stack>
  );
}

/** The user's own Materials Project API key for this project (when the site has none, or to use one's own). */
function MpKey({ projectId, info, open, setOpen }: { projectId: string; info: DbInfo; open: boolean; setOpen: (o: boolean) => void }) {
  const qc = useQueryClient();
  const [key, setKey] = useState("");
  const mp = info.databases.find((d) => d.id === "mp");
  const own = info.project_key;
  const refresh = () => qc.invalidateQueries({ queryKey: ["dft-databases", projectId] });
  const save = useMutation({ mutationFn: () => setProjectMpKey(projectId, key.trim()),
    onSuccess: () => { setKey(""); setOpen(false); void refresh(); } });
  const remove = useMutation({ mutationFn: () => removeProjectMpKey(projectId), onSuccess: () => void refresh() });
  return (
    <Stack gap={4} data-testid="mp-key">
      {own.stored ? (
        <Group gap="xs">
          <Text size="xs">{own.readable === false
            ? "Your Materials Project key for this project can no longer be read (the server's secret changed): enter it again."
            : `Materials Project: your own API key (${own.hint}) for this project.`}</Text>
          <Button size="compact-xs" variant="subtle" color="red" loading={remove.isPending} onClick={() => remove.mutate()}>Remove</Button>
        </Group>
      ) : !open && (
        <Anchor component="button" type="button" size="xs" onClick={() => setOpen(true)}>
          {mp?.available ? "Use your own Materials Project API key for this project…" : "Search the Materials Project with your own API key…"}
        </Anchor>
      )}
      {open && !own.stored && (
        <form onSubmit={(e) => { e.preventDefault(); if (key.trim()) save.mutate(); }}>
          <Stack gap={4}>
            <Text size="xs" c="dimmed">Your API key is on your <Anchor href="https://next-gen.materialsproject.org/api" target="_blank"
              rel="noreferrer" size="xs">Materials Project dashboard</Anchor>. It is checked with the Materials Project, kept encrypted
              for this project only, used by the server for your searches, and never shown again.</Text>
            <Group gap="xs">
              <PasswordInput size="xs" w={260} value={key} onChange={(e) => setKey(e.currentTarget.value)} aria-label="Materials Project API key"
                placeholder="API key" autoComplete="off" />
              <Button size="xs" type="submit" loading={save.isPending} disabled={!key.trim()}>Save the key</Button>
              <Button size="xs" variant="subtle" onClick={() => { setOpen(false); setKey(""); save.reset(); }}>Cancel</Button>
            </Group>
            <ErrorBox error={save.error} />
          </Stack>
        </form>
      )}
      <ErrorBox error={remove.error} />
    </Stack>
  );
}
